import { useMemo } from "react";
import { isPerpsExchange } from "./config";
import { useCexPerpsMarkets } from "./useCexPerpsMarkets";
import { useUnifiedTrade } from "@/store/unifiedTradeStore";
import { UnifiedMarketType } from "@/types/trading";

/**
 * Hook to get the currently selected CEX perps market
 * Reads from unified trade store and matches against available markets
 */
export function useCexPerpsMarket() {
  const { selectedMarket: unifiedMarket } = useUnifiedTrade();

  // Determine if current selection is a CEX perps market
  const exchangeId = useMemo(() => {
    if (unifiedMarket?.type === UnifiedMarketType.PERPS && isPerpsExchange(unifiedMarket.exchangeId)) {
      return unifiedMarket.exchangeId;
    }
    return null;
  }, [unifiedMarket]);

  // Fetch markets for the selected exchange
  const { data: markets = [], isLoading, error } = useCexPerpsMarkets(exchangeId ?? "", { enabled: !!exchangeId });

  // Find the selected market from the list
  const selectedMarket = useMemo(() => {
    if (!exchangeId || unifiedMarket?.type !== UnifiedMarketType.PERPS) {
      return null;
    }

    return markets.find((m) => m.pairSymbol === unifiedMarket.symbol) ?? null;
  }, [exchangeId, unifiedMarket, markets]);

  return {
    selectedMarket,
    allMarkets: markets,
    isLoading,
    error: error as Error | null,
    exchangeId,
  };
}
